import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { TrendingUp, MousePointerClick, FileText, Eye } from "lucide-react";

const WINDOWS = [[7, "7d"], [30, "30d"], [90, "90d"]];

export default function BoardAnalyticsDialog({ open, onClose, board }) {
    const [days, setDays] = useState(30);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open || !board) return;
        setLoading(true);
        api.get(`/embed-boards/${board.id}/analytics`, { params: { days } })
            .then((r) => setData(r.data))
            .catch((err) => toast.error(formatApiError(err)))
            .finally(() => setLoading(false));
    }, [open, board, days]);

    if (!board) return null;

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="rounded-none max-w-5xl w-[95vw] max-h-[92vh] overflow-y-auto" data-testid="board-analytics-dialog">
                <DialogHeader>
                    <DialogTitle>Board analytics · {board.name}</DialogTitle>
                    <DialogDescription>Views, job clicks, and applications captured from this board's iframe and apply page.</DialogDescription>
                </DialogHeader>

                <div className="flex items-center gap-2 mb-4">
                    {WINDOWS.map(([d, label]) => (
                        <button
                            key={d}
                            onClick={() => setDays(d)}
                            className={`px-3 py-1 text-xs font-mono uppercase tracking-widest border ${days === d ? "bg-ikb text-white border-ikb" : "bg-white border-border text-foreground/70"}`}
                            data-testid={`analytics-window-${d}`}
                        >
                            {label}
                        </button>
                    ))}
                    {data?.all_time && (
                        <div className="ml-auto text-[11px] font-mono uppercase tracking-widest text-foreground/60">
                            All time · views <strong>{data.all_time.views}</strong> · branding clicks <strong>{data.all_time.branding_clicks}</strong>
                        </div>
                    )}
                </div>

                {loading || !data ? (
                    <div className="border border-dashed border-border p-16 text-center text-sm text-muted-foreground">Loading…</div>
                ) : (
                    <div className="space-y-6">
                        {/* Funnel KPI tiles */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border" data-testid="analytics-funnel">
                            <Kpi icon={<Eye className="w-4 h-4" />} label="Views" value={data.funnel.views} />
                            <Kpi
                                icon={<MousePointerClick className="w-4 h-4" />}
                                label="Job clicks"
                                value={data.funnel.job_clicks}
                                subline={`${data.funnel.view_to_click_pct}% click-through`}
                            />
                            <Kpi
                                icon={<FileText className="w-4 h-4" />}
                                label="Applications"
                                value={data.funnel.applications}
                                subline={`${data.funnel.click_to_app_pct}% of clicks`}
                            />
                            <Kpi
                                icon={<TrendingUp className="w-4 h-4" />}
                                label="View → apply"
                                value={`${data.funnel.view_to_app_pct}%`}
                            />
                        </div>

                        {/* Daily line chart */}
                        {data.daily.length > 0 ? (
                            <section className="bg-white border border-border p-4">
                                <h3 className="font-display font-bold text-sm uppercase tracking-widest mb-3">Daily activity</h3>
                                <ResponsiveContainer width="100%" height={240}>
                                    <LineChart data={data.daily}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                        <XAxis dataKey="date" tick={{ fontSize: 10, fontFamily: "ui-monospace, monospace" }} />
                                        <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                                        <Tooltip contentStyle={{ borderRadius: 0, border: "1px solid #e2e8f0", fontSize: 12 }} />
                                        <Line type="monotone" dataKey="views" stroke="#64748b" strokeWidth={2} dot={false} />
                                        <Line type="monotone" dataKey="clicks" stroke="#002FA7" strokeWidth={2} dot={false} />
                                        <Line type="monotone" dataKey="applications" stroke="#16a34a" strokeWidth={2} dot={false} />
                                    </LineChart>
                                </ResponsiveContainer>
                                <Legend />
                            </section>
                        ) : (
                            <section className="bg-white border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
                                No activity yet in the last {days} days.
                            </section>
                        )}

                        {/* Per-tab funnel */}
                        {data.by_tab.length > 0 && (
                            <section className="bg-white border border-border" data-testid="analytics-by-tab">
                                <h3 className="font-display font-bold text-sm uppercase tracking-widest p-4 border-b border-border">Per-tab funnel</h3>
                                <table className="w-full text-sm">
                                    <thead className="bg-secondary text-foreground/70 text-[11px] font-mono uppercase tracking-widest">
                                        <tr>
                                            <th className="text-left p-3">Tab</th>
                                            <th className="text-right p-3">Tab views</th>
                                            <th className="text-right p-3">Clicks</th>
                                            <th className="text-right p-3">Applications</th>
                                            <th className="text-right p-3">Conv. %</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.by_tab.map((r) => (
                                            <tr key={r.tab_id} className="border-t border-border" data-testid={`analytics-tab-row-${r.tab_id}`}>
                                                <td className="p-3 font-medium">{r.tab_title}</td>
                                                <td className="p-3 text-right font-mono">{r.views}</td>
                                                <td className="p-3 text-right font-mono">{r.clicks}</td>
                                                <td className="p-3 text-right font-mono">{r.applications}</td>
                                                <td className="p-3 text-right font-mono">
                                                    {r.views ? `${((r.applications / r.views) * 100).toFixed(1)}%` : "—"}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </section>
                        )}

                        {/* Application source breakdown — Phase 54 */}
                        {(data.by_source?.length > 0 || data.top_referrers?.length > 0) && (
                            <section className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="analytics-by-source">
                                <div className="bg-white border border-border">
                                    <h3 className="font-display font-bold text-sm uppercase tracking-widest p-4 border-b border-border flex items-center justify-between">
                                        <span>Application source</span>
                                        <span className="text-[10px] font-mono text-muted-foreground">last {days}d</span>
                                    </h3>
                                    {(!data.by_source || data.by_source.length === 0) ? (
                                        <div className="p-6 text-center text-xs text-muted-foreground">No source data yet — enable the "Where did you find this job?" field on the Apply form.</div>
                                    ) : (
                                        <SourceBars rows={data.by_source} />
                                    )}
                                </div>
                                <div className="bg-white border border-border">
                                    <h3 className="font-display font-bold text-sm uppercase tracking-widest p-4 border-b border-border flex items-center justify-between">
                                        <span>Top referrers</span>
                                        <span className="text-[10px] font-mono text-muted-foreground">by name</span>
                                    </h3>
                                    {(!data.top_referrers || data.top_referrers.length === 0) ? (
                                        <div className="p-6 text-center text-xs text-muted-foreground">No referral names captured yet.</div>
                                    ) : (
                                        <ul className="divide-y divide-border">
                                            {data.top_referrers.map((r) => (
                                                <li key={r.name} className="px-4 py-2.5 flex items-center justify-between text-sm" data-testid={`analytics-referrer-${r.name}`}>
                                                    <span className="truncate">{r.name}</span>
                                                    <span className="font-mono text-foreground/70 tabular-nums">{r.count}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </section>
                        )}

                        {/* Top jobs */}
                        {data.top_jobs.length > 0 && (
                            <section className="bg-white border border-border" data-testid="analytics-top-jobs">
                                <h3 className="font-display font-bold text-sm uppercase tracking-widest p-4 border-b border-border">Top performing jobs</h3>
                                <table className="w-full text-sm">
                                    <thead className="bg-secondary text-foreground/70 text-[11px] font-mono uppercase tracking-widest">
                                        <tr>
                                            <th className="text-left p-3">Job</th>
                                            <th className="text-right p-3">Clicks</th>
                                            <th className="text-right p-3">Applications</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.top_jobs.map((r) => (
                                            <tr key={r.job_id} className="border-t border-border">
                                                <td className="p-3">
                                                    {r.slug ? (
                                                        <a href={`/jobs/${r.job_id}`} className="hover:text-ikb">{r.job_title || r.job_id}</a>
                                                    ) : (r.job_title || r.job_id)}
                                                </td>
                                                <td className="p-3 text-right font-mono">{r.clicks}</td>
                                                <td className="p-3 text-right font-mono">{r.applications}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </section>
                        )}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

function Kpi({ icon, label, value, subline }) {
    return (
        <div className="bg-white p-4">
            <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-foreground/70">
                {icon} {label}
            </div>
            <div className="font-display font-black text-2xl mt-2">{value}</div>
            {subline && <div className="text-[11px] font-mono text-foreground/60 mt-1">{subline}</div>}
        </div>
    );
}

function Legend() {
    return (
        <div className="flex items-center gap-4 mt-2 text-[11px] font-mono uppercase tracking-widest text-foreground/70">
            <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 inline-block" style={{ background: "#64748b" }} /> Views</span>
            <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 inline-block" style={{ background: "#002FA7" }} /> Clicks</span>
            <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 inline-block" style={{ background: "#16a34a" }} /> Applications</span>
        </div>
    );
}

function SourceBars({ rows }) {
    const total = rows.reduce((s, r) => s + r.count, 0) || 1;
    return (
        <ul className="divide-y divide-border" data-testid="analytics-source-bars">
            {rows.map((r) => {
                const pct = (r.count / total) * 100;
                return (
                    <li key={r.source} className="px-4 py-2.5" data-testid={`analytics-source-${r.source}`}>
                        <div className="flex items-baseline justify-between gap-3">
                            <span className="text-sm font-medium truncate">{r.source}</span>
                            <span className="font-mono text-[11px] text-foreground/70 tabular-nums">
                                {r.count} <span className="text-foreground/40">· {pct.toFixed(0)}%</span>
                            </span>
                        </div>
                        <div className="mt-1.5 h-1.5 bg-secondary border border-border overflow-hidden">
                            <div className="h-full bg-ikb" style={{ width: `${pct}%` }} />
                        </div>
                    </li>
                );
            })}
        </ul>
    );
}
